import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  Container,
  Pagination,
  Stack,
  InputBase
} from "@mui/material";
import {
  Add,
  Payment,
  Receipt,
  ArrowBack,
  Schedule,
  CheckCircle,
  Pending,
  Error as ErrorIcon,
  TrendingUp,
  TrendingDown,
  AccountBalance,
  ReceiptLong,
  Visibility,
  Edit,
  TrendingFlat,
  AccountBalanceWallet,
  Search
} from "@mui/icons-material";
import { suppliersAPI } from "../../services/API";
import { useSnackbar } from "notistack";

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`tabpanel-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
  </div>
);

// Payment Type Cards Component
const PaymentTypeCard = React.memo(({
  type,
  icon,
  title,
  description,
  isSelected,
  onClick,
}) => (
  <Card
    sx={{
      cursor: "pointer",
      border: isSelected ? 2 : 1,
      borderColor: isSelected ? "primary.main" : "grey.300",
      transition: "all 0.2s",
      borderRadius: 3,
      boxShadow: 2,
      "&:hover": {
        borderColor: "primary.main",
        transform: "translateY(-2px)",
        boxShadow: 4,
      },
    }}
    onClick={onClick}
  >
    <CardContent sx={{ textAlign: "center", p: 2 }}>
      {icon}
      <Typography variant="h6" sx={{ mt: 1, fontSize: '1.2rem', fontWeight: '600' }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '1rem' }}>
        {description}
      </Typography>
    </CardContent>
  </Card>
));

// Multi-step Payment Dialog Component
const PaymentDialog = React.memo(({ open, onClose, supplierId, onPaymentAdded }) => {
  const [step, setStep] = useState(1);
  const [paymentType, setPaymentType] = useState("");
  const [unpaidGrns, setUnpaidGrns] = useState([]);
  const [loading, setLoading] = useState(false);

  const [paymentData, setPaymentData] = useState({
    amount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMethod: "",
    paymentType: "",
    reference: "",
    notes: "",
    status: "completed",
    grnId: "",
    purchaseOrder: "",
    adjustmentType: "none",
    adjustmentReason: "",
  });

  useEffect(() => {
    if (open && paymentType === "grn_payment") {
      fetchUnpaidGRNs();
    }
  }, [open, paymentType]);

  const fetchUnpaidGRNs = async () => {
    try {
      const response = await suppliersAPI.getUnpaidGRNs(supplierId);
      setUnpaidGrns(response.data.data.unpaidGRNs || []);
    } catch (error) {
      console.error("Error fetching unpaid GRNs:", error);
    }
  };

  const handleInputChange = (field, value) => {
    setPaymentData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await suppliersAPI.addPayment(supplierId, paymentData);
      onPaymentAdded();
      onClose();
      resetForm();
    } catch (error) {
      console.error("Error recording payment:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setPaymentType("");
    setPaymentData({
      amount: "",
      paymentDate: new Date().toISOString().split("T")[0],
      paymentMethod: "",
      paymentType: "",
      reference: "",
      notes: "",
      status: "completed",
      grnId: "",
      purchaseOrder: "",
      adjustmentType: "none",
      adjustmentReason: "",
    });
  };

  const renderStep1 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
        Select Payment Type
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <PaymentTypeCard
            type="grn_payment"
            icon={<Receipt sx={{ fontSize: 40, color: "primary.main" }} />}
            title="GRN Payment"
            description="Pay against received goods"
            isSelected={paymentType === "grn_payment"}
            onClick={() => {
              setPaymentType("grn_payment");
              handleInputChange("paymentType", "grn_payment");
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <PaymentTypeCard
            type="advance_payment"
            icon={<TrendingUp sx={{ fontSize: 40, color: "warning.main" }} />}
            title="Advance Payment"
            description="Pay before goods receipt"
            isSelected={paymentType === "advance_payment"}
            onClick={() => {
              setPaymentType("advance_payment");
              handleInputChange("paymentType", "advance_payment");
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <PaymentTypeCard
            type="return_settlement"
            icon={<TrendingDown sx={{ fontSize: 40, color: "success.main" }} />}
            title="Return Settlement"
            description="Credit from returned goods"
            isSelected={paymentType === "return_settlement"}
            onClick={() => {
              setPaymentType("return_settlement");
              handleInputChange("paymentType", "return_settlement");
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <PaymentTypeCard
            type="account_settlement"
            icon={<AccountBalance sx={{ fontSize: 40, color: "info.main" }} />}
            title="Account Settlement"
            description="Clear outstanding balance"
            isSelected={paymentType === "account_settlement"}
            onClick={() => {
              setPaymentType("account_settlement");
              handleInputChange("paymentType", "account_settlement");
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
        Payment Details - {paymentType.replace("_", " ").toUpperCase()}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="number"
            label="Amount"
            value={paymentData.amount}
            onChange={(e) =>
              handleInputChange("amount", parseFloat(e.target.value) || 0)
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">₹</InputAdornment>
              ),
            }}
            required
            sx={{ borderRadius: 2, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Payment Date"
            value={paymentData.paymentDate}
            onChange={(e) => handleInputChange("paymentDate", e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
            sx={{ borderRadius: 2, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Payment Method"
            value={paymentData.paymentMethod}
            onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
            required
            sx={{ borderRadius: 2, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          >
            <MenuItem value="bank_transfer" sx={{ fontSize: '1rem' }}>Bank Transfer</MenuItem>
            <MenuItem value="cheque" sx={{ fontSize: '1rem' }}>Cheque</MenuItem>
            <MenuItem value="cash" sx={{ fontSize: '1rem' }}>Cash</MenuItem>
            <MenuItem value="upi" sx={{ fontSize: '1rem' }}>UPI</MenuItem>
            <MenuItem value="card" sx={{ fontSize: '1rem' }}>Card</MenuItem>
            <MenuItem value="online" sx={{ fontSize: '1rem' }}>Online Payment</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            select
            label="Status"
            value={paymentData.status}
            onChange={(e) => handleInputChange("status", e.target.value)}
            sx={{ borderRadius: 2, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          >
            <MenuItem value="pending" sx={{ fontSize: '1rem' }}>Pending</MenuItem>
            <MenuItem value="completed" sx={{ fontSize: '1rem' }}>Completed</MenuItem>
            <MenuItem value="failed" sx={{ fontSize: '1rem' }}>Failed</MenuItem>
          </TextField>
        </Grid>

        {/* GRN Selection for GRN Payments */}
        {paymentType === "grn_payment" && (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontSize: '1.1rem' }}>Select GRN</InputLabel>
              <Select
                value={paymentData.grnId || ""}
                onChange={(e) => handleInputChange("grnId", e.target.value)}
                label="Select GRN"
                sx={{ borderRadius: 2, '& .MuiSelect-select': { fontSize: '1.1rem' } }}
              >
                <MenuItem value="" sx={{ fontSize: '1rem' }}>No GRN Link</MenuItem>
                {unpaidGrns.map((grn) => (
                  <MenuItem key={grn._id} value={grn._id} sx={{ fontSize: '1rem' }}>
                    {grn.grnNumber} - ₹{grn.totalAmount?.toLocaleString()} -{" "}
                    {grn.purchaseOrder?.poNumber}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Reference Number"
            value={paymentData.reference}
            onChange={(e) => handleInputChange("reference", e.target.value)}
            placeholder="Transaction ID, Cheque No., etc."
            sx={{ borderRadius: 2, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={paymentData.notes}
            onChange={(e) => handleInputChange("notes", e.target.value)}
            placeholder="Additional payment details..."
            sx={{ borderRadius: 2, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
        Review Payment
      </Typography>
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Type
              </Typography>
              <Typography sx={{ fontSize: '1.1rem' }}>
                {paymentData.paymentType?.replace("_", " ").toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Amount
              </Typography>
              <Typography variant="h6" color="primary.main" sx={{ fontSize: '1.2rem' }}>
                ₹{paymentData.amount?.toLocaleString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Date
              </Typography>
              <Typography sx={{ fontSize: '1.1rem' }}>
                {new Date(paymentData.paymentDate).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Method
              </Typography>
              <Typography sx={{ fontSize: '1.1rem' }}>
                {paymentData.paymentMethod?.replace("_", " ").toUpperCase()}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Status
              </Typography>
              <Chip
                label={paymentData.status}
                size="small"
                color={
                  paymentData.status === "completed" ? "success" : "warning"
                }
                sx={{ borderRadius: 2, fontSize: '0.9rem' }}
              />
            </Grid>
            {paymentData.reference && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                  Reference
                </Typography>
                <Typography sx={{ fontSize: '1.1rem' }}>{paymentData.reference}</Typography>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
        Record New Payment
        <Stepper activeStep={step - 1} sx={{ mt: 2 }}>
          <Step>
            <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '1rem' } }}>Type</StepLabel>
          </Step>
          <Step>
            <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '1rem' } }}>Details</StepLabel>
          </Step>
          <Step>
            <StepLabel sx={{ '& .MuiStepLabel-label': { fontSize: '1rem' } }}>Review</StepLabel>
          </Step>
        </Stepper>
      </DialogTitle>

      <DialogContent>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={step > 1 ? () => setStep(step - 1) : onClose}
          disabled={loading}
          sx={{ borderRadius: 2, fontSize: '1rem' }}
        >
          {step > 1 ? "Back" : "Cancel"}
        </Button>
        <Button
          variant="contained"
          onClick={() => (step < 3 ? setStep(step + 1) : handleSubmit())}
          disabled={!paymentType || loading}
          sx={{ borderRadius: 2, fontSize: '1rem' }}
        >
          {loading ? "Processing..." : step < 3 ? "Next" : "Record Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

// Enhanced Payment Table Component with Pagination
const PaymentTable = React.memo(({
  payments,
  onView,
  getStatusColor,
  getStatusIcon,
  getPaymentMethodColor,
  getPaymentTypeColor,
  pagination,
  onPageChange,
  loading
}) => {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ fontSize: '1.3rem' }}>
            Payment History ({pagination.total})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Page {pagination.current} of {pagination.pages}
          </Typography>
        </Box>

        {loading ? (
          <LinearProgress />
        ) : payments.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: '1rem' }}>
            No payment records found.
          </Alert>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Payment #</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Date</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Type</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Reference</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>GRN</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Method</TableCell>
                    <TableCell align="right" sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Amount</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Status</TableCell>
                    <TableCell sx={{ fontSize: '1.05rem', fontWeight: 'bold', py: 2 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => (
                    <TableRow key={payment._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.95rem' }}>
                          {payment.paymentNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.95rem', py: 1.5 }}>
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={payment.paymentType?.replace(/_/g, " ") || "Payment"}
                          size="small"
                          color={getPaymentTypeColor(payment.paymentType)}
                          variant="outlined"
                          sx={{ borderRadius: 2, fontSize: '0.85rem', height: '24px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                          {payment.reference || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        {payment.grn ? (
                          <Chip
                            label={payment.grn.grnNumber}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ borderRadius: 2, fontSize: '0.85rem', height: '24px' }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.95rem' }}>
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={payment.paymentMethod?.replace(/_/g, " ")}
                          size="small"
                          color={getPaymentMethodColor(payment.paymentMethod)}
                          variant="outlined"
                          sx={{ borderRadius: 2, fontSize: '0.85rem', height: '24px' }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ py: 1.5 }}>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ fontSize: '0.95rem' }}
                          color={
                            ["return_settlement", "credit_note"].includes(payment.paymentType)
                              ? "success.main"
                              : "primary.main"
                          }
                        >
                          {["return_settlement", "credit_note"].includes(payment.paymentType) ? "+" : ""}
                          ₹{payment.amount?.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          icon={getStatusIcon(payment.status)}
                          label={payment.status}
                          size="small"
                          color={getStatusColor(payment.status)}
                          sx={{ borderRadius: 2, fontSize: '0.85rem', height: '24px' }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => onView(payment)}
                            color="primary"
                            sx={{ borderRadius: 2 }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Stack spacing={2}>
                  <Pagination
                    count={pagination.pages}
                    page={pagination.current}
                    onChange={onPageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
});

// Search Component
const SearchComponent = React.memo(({ 
  searchTerm, 
  onSearchChange, 
  onSearchSubmit, 
  itemsPerPage, 
  onItemsPerPageChange,
  currentTab 
}) => {
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSearchSubmit();
    }
  };

  return (
    <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <Paper
              component="form"
              sx={{ 
                p: '2px 4px', 
                display: 'flex', 
                alignItems: 'center', 
                borderRadius: 2,
                boxShadow: 1
              }}
            >
              <InputBase
                sx={{ ml: 1, flex: 1, fontSize: '1rem' }}
                placeholder={`Search ${currentTab} payments...`}
                value={searchTerm}
                onChange={onSearchChange}
                onKeyPress={handleKeyPress}
              />
              <IconButton 
                type="button" 
                sx={{ p: '10px' }} 
                onClick={onSearchSubmit}
              >
                <Search />
              </IconButton>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '1rem' }}>Items per page</InputLabel>
                <Select
                  value={itemsPerPage}
                  label="Items per page"
                  onChange={onItemsPerPageChange}
                  sx={{ fontSize: '1rem' }}
                >
                  <MenuItem value={5} sx={{ fontSize: '1rem' }}>5</MenuItem>
                  <MenuItem value={10} sx={{ fontSize: '1rem' }}>10</MenuItem>
                  <MenuItem value={25} sx={{ fontSize: '1rem' }}>25</MenuItem>
                  <MenuItem value={50} sx={{ fontSize: '1rem' }}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

export const SupplierPayments = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // State for each tab's data
  const [paymentsData, setPaymentsData] = useState({
    all: { payments: [], pagination: { current: 1, pages: 1, total: 0, limit: 10 } },
    grn: { payments: [], pagination: { current: 1, pages: 1, total: 0, limit: 10 } },
    advance: { payments: [], pagination: { current: 1, pages: 1, total: 0, limit: 10 } },
    settlement: { payments: [], pagination: { current: 1, pages: 1, total: 0, limit: 10 } },
    pending: { payments: [], pagination: { current: 1, pages: 1, total: 0, limit: 10 } }
  });
  
  const [loading, setLoading] = useState({
    all: false,
    grn: false,
    advance: false,
    settlement: false,
    pending: false
  });
  
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [financialSummary, setFinancialSummary] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState({
    all: "",
    grn: "",
    advance: "",
    settlement: "",
    pending: ""
  });

  // Get current tab key
  const getCurrentTabKey = useCallback(() => {
    const tabKeys = ['all', 'grn', 'advance', 'settlement', 'pending'];
    return tabKeys[tabValue];
  }, [tabValue]);

  // Memoized fetch function
  const fetchTabData = useCallback(async (tabKey, page = paymentsData[tabKey].pagination.current, search = searchTerm[tabKey]) => {
    try {
      setLoading(prev => ({ ...prev, [tabKey]: true }));
      
      const params = {
        page,
        limit: paymentsData[tabKey].pagination.limit,
        search: search || "",
        tab: tabKey
      };

      const response = await suppliersAPI.getPayments(id, params);
      
      setPaymentsData(prev => ({
        ...prev,
        [tabKey]: {
          payments: response.data.data.payments || [],
          pagination: response.data.data.pagination || {
            current: page,
            pages: 1,
            total: 0,
            limit: paymentsData[tabKey].pagination.limit
          }
        }
      }));

    } catch (error) {
      enqueueSnackbar(`Error fetching ${tabKey} payments`, { variant: "error" });
      console.error(`Error fetching ${tabKey} payments:`, error);
    } finally {
      setLoading(prev => ({ ...prev, [tabKey]: false }));
    }
  }, [id, enqueueSnackbar]);

  // Fetch data when tab changes
  useEffect(() => {
    const currentTab = getCurrentTabKey();
    fetchTabData(currentTab, 1, searchTerm[currentTab]);
  }, [tabValue, getCurrentTabKey, fetchTabData]);

  // Fetch financial summary once
  useEffect(() => {
    const fetchFinancialSummary = async () => {
      try {
        const response = await suppliersAPI.getFinancialSummary(id);
        setFinancialSummary(response.data.data);
      } catch (error) {
        console.error("Error fetching financial summary:", error);
      }
    };
    fetchFinancialSummary();
  }, [id]);

  // Debounced search - only affects current tab
  useEffect(() => {
    const currentTab = getCurrentTabKey();
    const currentSearchTerm = searchTerm[currentTab];
    
    const timer = setTimeout(() => {
      if (currentSearchTerm !== "") {
        fetchTabData(currentTab, 1, currentSearchTerm);
      } else if (currentSearchTerm === "" && paymentsData[currentTab].payments.length === 0) {
        // Only fetch if we don't have data
        fetchTabData(currentTab, 1, "");
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [searchTerm, getCurrentTabKey, fetchTabData]);

  const handlePaymentAdded = useCallback(() => {
    enqueueSnackbar("Payment recorded successfully", { variant: "success" });
    
    // Refresh current tab only
    const currentTab = getCurrentTabKey();
    fetchTabData(currentTab, 1, searchTerm[currentTab]);
    
    // Refresh financial summary
    suppliersAPI.getFinancialSummary(id).then(response => {
      setFinancialSummary(response.data.data);
    });
  }, [enqueueSnackbar, getCurrentTabKey, fetchTabData, searchTerm, id]);

  const handleViewPayment = useCallback((payment) => {
    setSelectedPayment(payment);
    setViewDialog(true);
  }, []);

  const handlePageChange = useCallback((event, value, tabKey) => {
    setPaymentsData(prev => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        pagination: { ...prev[tabKey].pagination, current: value }
      }
    }));
    fetchTabData(tabKey, value, searchTerm[tabKey]);
  }, []);

  const handleSearchChange = useCallback((event, tabKey) => {
    setSearchTerm(prev => ({
      ...prev,
      [tabKey]: event.target.value
    }));
  }, []);

  const handleSearchSubmit = useCallback((tabKey) => {
    fetchTabData(tabKey, 1, searchTerm[tabKey]);
  }, [fetchTabData, searchTerm]);

  const handleItemsPerPageChange = useCallback((value, tabKey) => {
    setPaymentsData(prev => ({ 
      ...prev, 
      [tabKey]: { 
        ...prev[tabKey], 
        pagination: { ...prev[tabKey].pagination, limit: value, current: 1 } 
      } 
    }));
  }, []);

  // Memoized helper functions
  const getStatusColor = useCallback((status) => {
    const colors = {
      completed: "success",
      pending: "warning",
      failed: "error",
      draft: "default",
      cancelled: "error",
    };
    return colors[status] || "default";
  }, []);

  const getStatusIcon = useCallback((status) => {
    const icons = {
      completed: <CheckCircle />,
      pending: <Pending />,
      failed: <ErrorIcon />,
      draft: <Schedule />,
      cancelled: <ErrorIcon />,
    };
    return icons[status] || <Pending />;
  }, []);

  const getPaymentMethodColor = useCallback((method) => {
    const colors = {
      bank_transfer: "primary",
      cheque: "secondary",
      cash: "success",
      upi: "info",
      card: "warning",
      online: "info",
      credit_note: "success",
    };
    return colors[method] || "default";
  }, []);

  const getPaymentTypeColor = useCallback((type) => {
    const colors = {
      grn_payment: "primary",
      advance_payment: "info",
      return_settlement: "success",
      credit_note: "success",
      debit_note: "error",
      account_settlement: "secondary",
    };
    return colors[type] || "default";
  }, []);

  const currentTabKey = getCurrentTabKey();

  return (
    <Box sx={{ minHeight: "100vh", background: "linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)" }}>
      <Container maxWidth="xl" sx={{ py: 5 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate("/hospital/pharmacy/suppliers")}
              variant="outlined"
              sx={{ borderRadius: 2, fontSize: '1rem' }}
            >
              Back to Suppliers
            </Button>
            <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '2.2rem' }}>
              Payments & Settlements
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setPaymentDialog(true)}
            sx={{ 
              borderRadius: 2, 
              boxShadow: 2, 
              transition: "0.3s", 
              "&:hover": { transform: "scale(1.03)" },
              fontSize: '1rem'
            }}
          >
            Record Payment
          </Button>
        </Box>

        {/* Financial Summary - Only re-renders when financialSummary changes */}
        {financialSummary && (
          <FinancialSummary financialSummary={financialSummary} />
        )}

        {/* Search Component - Only re-renders when search term or items per page changes */}
        <SearchComponent
          searchTerm={searchTerm[currentTabKey]}
          onSearchChange={(e) => handleSearchChange(e, currentTabKey)}
          onSearchSubmit={() => handleSearchSubmit(currentTabKey)}
          itemsPerPage={paymentsData[currentTabKey].pagination.limit}
          onItemsPerPageChange={(e) => handleItemsPerPageChange(e.target.value, currentTabKey)}
          currentTab={currentTabKey}
        />

        {/* Tabs - Only re-renders when tab value changes */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <CardContent>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
            >
              <Tab label="All Payments" sx={{ fontSize: '1rem' }} />
              <Tab label="GRN Payments" sx={{ fontSize: '1rem' }} />
              <Tab label="Advance Payments" sx={{ fontSize: '1rem' }} />
              <Tab label="Settlements" sx={{ fontSize: '1rem' }} />
              <Tab label="Pending" sx={{ fontSize: '1rem' }} />
            </Tabs>
          </CardContent>
        </Card>

        {/* Tab Panels - Each panel is independent */}
        <TabPanel value={tabValue} index={0}>
          <PaymentTable
            payments={paymentsData.all.payments}
            onView={handleViewPayment}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            getPaymentMethodColor={getPaymentMethodColor}
            getPaymentTypeColor={getPaymentTypeColor}
            pagination={paymentsData.all.pagination}
            onPageChange={(event, value) => handlePageChange(event, value, 'all')}
            loading={loading.all}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <PaymentTable
            payments={paymentsData.grn.payments}
            onView={handleViewPayment}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            getPaymentMethodColor={getPaymentMethodColor}
            getPaymentTypeColor={getPaymentTypeColor}
            pagination={paymentsData.grn.pagination}
            onPageChange={(event, value) => handlePageChange(event, value, 'grn')}
            loading={loading.grn}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <PaymentTable
            payments={paymentsData.advance.payments}
            onView={handleViewPayment}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            getPaymentMethodColor={getPaymentMethodColor}
            getPaymentTypeColor={getPaymentTypeColor}
            pagination={paymentsData.advance.pagination}
            onPageChange={(event, value) => handlePageChange(event, value, 'advance')}
            loading={loading.advance}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <PaymentTable
            payments={paymentsData.settlement.payments}
            onView={handleViewPayment}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            getPaymentMethodColor={getPaymentMethodColor}
            getPaymentTypeColor={getPaymentTypeColor}
            pagination={paymentsData.settlement.pagination}
            onPageChange={(event, value) => handlePageChange(event, value, 'settlement')}
            loading={loading.settlement}
          />
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <PaymentTable
            payments={paymentsData.pending.payments}
            onView={handleViewPayment}
            getStatusColor={getStatusColor}
            getStatusIcon={getStatusIcon}
            getPaymentMethodColor={getPaymentMethodColor}
            getPaymentTypeColor={getPaymentTypeColor}
            pagination={paymentsData.pending.pagination}
            onPageChange={(event, value) => handlePageChange(event, value, 'pending')}
            loading={loading.pending}
          />
        </TabPanel>

        {/* Dialogs */}
        <PaymentDialog
          open={paymentDialog}
          onClose={() => setPaymentDialog(false)}
          supplierId={id}
          onPaymentAdded={handlePaymentAdded}
        />

        <ViewPaymentDialog
          open={viewDialog}
          onClose={() => setViewDialog(false)}
          payment={selectedPayment}
          getStatusColor={getStatusColor}
          getStatusIcon={getStatusIcon}
          getPaymentMethodColor={getPaymentMethodColor}
          getPaymentTypeColor={getPaymentTypeColor}
        />
      </Container>
    </Box>
  );
};

// Additional memoized components
const FinancialSummary = React.memo(({ financialSummary }) => (
  <Grid container spacing={3} sx={{ mb: 3 }}>
    <Grid item xs={12} sm={6} md={2.4}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
        <CardContent sx={{ textAlign: "center" }}>
          <ReceiptLong sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="primary.main" sx={{ fontSize: '2rem' }}>
            ₹{financialSummary.financialSummary?.totalGRNValue?.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Total GRN Value
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={2.4}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
        <CardContent sx={{ textAlign: "center" }}>
          <Payment sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="success.main" sx={{ fontSize: '2rem' }}>
            ₹{financialSummary.financialSummary?.totalPayments?.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Total Paid
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={2.4}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
        <CardContent sx={{ textAlign: "center" }}>
          <AccountBalanceWallet sx={{ fontSize: 40, color: "warning.main", mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="warning.main" sx={{ fontSize: '2rem' }}>
            ₹{financialSummary.financialSummary?.outstandingBalance?.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Outstanding
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={2.4}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
        <CardContent sx={{ textAlign: "center" }}>
          <TrendingDown sx={{ fontSize: 40, color: "info.main", mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="info.main" sx={{ fontSize: '2rem' }}>
            ₹{financialSummary.financialSummary?.availableCredits?.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Available Credits
          </Typography>
        </CardContent>
      </Card>
    </Grid>

    <Grid item xs={12} sm={6} md={2.4}>
      <Card sx={{ borderRadius: 3, boxShadow: 3, transition: "0.3s", "&:hover": { boxShadow: 6, transform: "translateY(-5px)" } }}>
        <CardContent sx={{ textAlign: "center" }}>
          <AccountBalance sx={{ fontSize: 40, color: "secondary.main", mb: 1 }} />
          <Typography variant="h4" fontWeight="bold" color="secondary.main" sx={{ fontSize: '2rem' }}>
            ₹{financialSummary.financialSummary?.netPayable?.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
            Net Payable
          </Typography>
        </CardContent>
      </Card>
    </Grid>
  </Grid>
));

const ViewPaymentDialog = React.memo(({ 
  open, 
  onClose, 
  payment, 
  getStatusColor, 
  getStatusIcon, 
  getPaymentMethodColor, 
  getPaymentTypeColor 
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="md"
    fullWidth
    PaperProps={{ sx: { borderRadius: 3 } }}
  >
    <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>Payment Details</DialogTitle>
    <DialogContent>
      {payment && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
              Payment Number
            </Typography>
            <Typography variant="h6" sx={{ fontSize: '1.2rem' }}>
              {payment.paymentNumber}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
              Amount
            </Typography>
            <Typography variant="h6" color="primary.main" sx={{ fontSize: '1.2rem' }}>
              ₹{payment.amount?.toLocaleString()}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
              Payment Type
            </Typography>
            <Chip
              label={payment.paymentType?.replace(/_/g, " ")}
              color={getPaymentTypeColor(payment.paymentType)}
              size="small"
              sx={{ borderRadius: 2, fontSize: '0.9rem' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
              Status
            </Typography>
            <Chip
              icon={getStatusIcon(payment.status)}
              label={payment.status}
              color={getStatusColor(payment.status)}
              size="small"
              sx={{ borderRadius: 2, fontSize: '0.9rem' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
              Payment Method
            </Typography>
            <Chip
              label={payment.paymentMethod?.replace(/_/g, " ")}
              color={getPaymentMethodColor(payment.paymentMethod)}
              size="small"
              sx={{ borderRadius: 2, fontSize: '0.9rem' }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
              Date
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '1rem' }}>
              {new Date(payment.paymentDate).toLocaleDateString()}
            </Typography>
          </Grid>
          {payment.grn && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Linked GRN
              </Typography>
              <Chip
                label={payment.grn.grnNumber}
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2, fontSize: '0.9rem' }}
              />
            </Grid>
          )}
          {payment.reference && (
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Reference
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                {payment.reference}
              </Typography>
            </Grid>
          )}
          {payment.notes && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: '1.05rem' }}>
                Notes
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '1rem' }}>
                {payment.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} sx={{ borderRadius: 2, fontSize: '1rem' }}>Close</Button>
    </DialogActions>
  </Dialog>
));